import { useMemo, useState, type FormEvent } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Check, Lock, PlugZap } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import type {
  PluginConfigFieldDto,
  PluginConfigGroupDto,
  PluginSummaryDto,
  MarketplaceConnectorStatusMap,
} from "../../dtos/marketplace.dto"

// Outcome of a submit: ok advances to the success screen; otherwise the wizard
// stays open and shows `error` inline so the user can fix and retry.
export interface SubmitOutcome {
  ok: boolean
  error?: string
}

interface PluginInstallWizardProps {
  mode: "install" | "reconfigure"
  plugin: PluginSummaryDto
  // Per-org connector status, so integration-backed groups (e.g. Sage 300) can
  // render as "already connected" (pull-across) instead of re-collecting.
  connectorStatuses: MarketplaceConnectorStatusMap
  // Existing values for a reconfigure/tier-switch flow. Secret fields with an
  // existing (masked) value can be left blank to keep them unchanged.
  initialValues?: Record<string, string>
  isSubmitting: boolean
  // Runs the install/update and reports the outcome, so the wizard can decide
  // whether to show the success screen or surface the error and let the user retry.
  onSubmit: (values: Record<string, string>) => Promise<SubmitOutcome>
  onClose: () => void
}

const DEFAULT_GROUP_ID = -1

interface ConfigStep {
  group: PluginConfigGroupDto
  fields: PluginConfigFieldDto[]
}

// A step-by-step walkthrough for installing or reconfiguring a plugin. Steps come
// from the manifest's groups (one step per group). A group backed by an internal
// connector (group.connector) is either shown read-only as "already connected"
// (its secrets stay in the connector's own store) or collects credentials to be
// provisioned server-side. Everything is manifest-driven — no per-plugin code.
export function PluginInstallWizard({
  mode,
  plugin,
  connectorStatuses,
  initialValues = {},
  isSubmitting,
  onSubmit,
  onClose,
}: PluginInstallWizardProps) {
  const hasExisting = (key: string) => Boolean(initialValues[key])

  const isConnected = (group: PluginConfigGroupDto) =>
    Boolean(group.connector && connectorStatuses[group.connector]?.connected)

  // Build ordered steps from the manifest groups. Fields not assigned to any
  // defined group collapse into a single default step, so we never regress to
  // one-field-per-step.
  const steps = useMemo<ConfigStep[]>(() => {
    const groups = plugin.configSchema.groups ?? []
    const fields = plugin.configSchema.fields ?? []

    const result: ConfigStep[] = []
    const assigned = new Set<string>()

    for (const group of groups) {
      const groupFields = fields.filter((f) => f.group === group.id)
      groupFields.forEach((f) => assigned.add(f.key))
      if (groupFields.length > 0) result.push({ group, fields: groupFields })
    }

    const orphans = fields.filter((f) => !assigned.has(f.key))
    if (orphans.length > 0)
      result.push({ group: { id: DEFAULT_GROUP_ID, title: "Configuration" }, fields: orphans })

    return result
  }, [plugin])

  // Step 0 = overview, 1..N = one per group, N+1 = review.
  const reviewStep = steps.length + 1
  const totalSteps = reviewStep + 1
  const [step, setStep] = useState(0)
  const [succeeded, setSucceeded] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const schema = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {}

    for (const { group, fields } of steps) {
      // Connected connector groups aren't collected, so their fields are optional.
      const skip = isConnected(group)
      for (const field of fields) {
        const canBeBlank = skip || !field.required || (field.secret && hasExisting(field.key))
        shape[field.key] = canBeBlank
          ? z.string().optional()
          : z.string().min(1, `${field.label} is required`)
      }
    }

    return z.object(shape)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, connectorStatuses])

  const form = useForm<Record<string, string>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    defaultValues: (plugin.configSchema.fields ?? []).reduce<Record<string, string>>((acc, field) => {
      acc[field.key] = field.secret ? "" : (initialValues[field.key] ?? "")
      return acc
    }, {}),
  })

  const buildPayload = (values: Record<string, string>) => {
    const payload: Record<string, string> = {}

    for (const { group, fields } of steps) {
      // Connected connector group → omit; the backend pulls it across from the
      // connector's own store and never expects its secrets here.
      if (isConnected(group)) continue

      for (const field of fields) {
        const value = values[field.key] ?? ""

        // Blank secret field with an existing value means "keep unchanged".
        if (field.secret && value === "" && hasExisting(field.key)) continue

        payload[field.key] = value
      }
    }

    return payload
  }

  const goNext = async () => {
    setSubmitError(null)
    if (step === 0) {
      setStep(1)
      return
    }

    const { group, fields } = steps[step - 1]
    if (isConnected(group)) {
      setStep(step + 1)
      return
    }

    const valid = await form.trigger(fields.map((f) => f.key))
    if (valid) setStep(step + 1)
  }

  const goBack = () => {
    setSubmitError(null)
    setStep(Math.max(0, step - 1))
  }

  const handleConfirm = form.handleSubmit(async (values) => {
    setSubmitError(null)
    const outcome = await onSubmit(buildPayload(values))
    if (outcome.ok) setSucceeded(true)
    else setSubmitError(outcome.error ?? "Something went wrong. Please try again.")
  })

  // A raw form submit (e.g. pressing Enter in a field) never installs — it only
  // advances through the steps. Install/Save happens exclusively via the explicit
  // action button's onClick on the final review step, so nothing auto-submits.
  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (step < reviewStep) void goNext()
  }

  const actionLabel = mode === "install" ? "Install" : "Save"
  const verb = mode === "install" ? "installed" : "updated"

  const currentStep = step > 0 && step <= steps.length ? steps[step - 1] : undefined

  return (
    <Dialog open onOpenChange={(next) => !next && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-lg bg-card">
        {succeeded ? (
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <div className="rounded-full bg-primary/10 p-3">
              <Check className="w-6 h-6 text-primary" />
            </div>
            <DialogTitle>{plugin.name} {verb}</DialogTitle>
            <DialogDescription>
              {mode === "install"
                ? `${plugin.name} is now set up for your organization.`
                : `Your changes to ${plugin.name} have been saved.`}
            </DialogDescription>
            <Button className="mt-2" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {mode === "install" ? `Install ${plugin.name}` : `Update ${plugin.name}`}
              </DialogTitle>
              <DialogDescription>
                {step === 0
                  ? `Step 1 of ${totalSteps} · Overview`
                  : step === reviewStep
                    ? `Step ${step + 1} of ${totalSteps} · Review`
                    : `Step ${step + 1} of ${totalSteps} · ${currentStep?.group.title}`}
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-border"}`}
                />
              ))}
            </div>

            <Form {...form}>
              <form onSubmit={handleFormSubmit} className="space-y-4" autoComplete="off">
                {step === 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">{plugin.description}</p>
                    <div className="rounded-lg border border-border p-3 space-y-3">
                      <p className="text-sm font-medium">What you'll need:</p>
                      {steps.map(({ group, fields }) => (
                        <div key={group.id} className="space-y-1.5">
                          {steps.length > 1 && (
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {group.title}
                            </p>
                          )}
                          {isConnected(group) ? (
                            <p className="flex items-center gap-2 text-sm text-primary">
                              <Check className="w-3.5 h-3.5 shrink-0" />
                              Already connected — nothing to enter
                            </p>
                          ) : (
                            <ul className="space-y-1.5">
                              {fields.map((f) => (
                                <li key={f.key} className="flex items-center gap-2 text-sm text-muted-foreground">
                                  {f.secret && <Lock className="w-3.5 h-3.5 shrink-0" />}
                                  {f.label}
                                  {hasExisting(f.key) && (
                                    <span className="text-xs text-primary">(already set)</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {currentStep && (
                  isConnected(currentStep.group) ? (
                    <ConnectedConnectorPanel
                      group={currentStep.group}
                      displayValues={connectorStatuses[currentStep.group.connector!]?.displayValues ?? {}}
                    />
                  ) : (
                    <div className="space-y-4">
                      {currentStep.group.connector && (
                        <p className="text-sm text-muted-foreground">
                          Enter your {currentStep.group.title} details. We'll verify and store them
                          securely in your integration — they're never shared with the plugin.
                        </p>
                      )}
                      {currentStep.fields.map((field, index) => (
                        <FieldInput
                          key={field.key}
                          field={field}
                          form={form}
                          hasExisting={hasExisting}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                  )
                )}

                {step === reviewStep && (
                  <div className="space-y-4">
                    <p className="text-sm font-medium">Review your details</p>
                    {steps.map(({ group, fields }) => (
                      <div key={group.id} className="space-y-1">
                        {steps.length > 1 && (
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {group.title}
                          </p>
                        )}
                        {isConnected(group) ? (
                          <div className="flex items-center justify-between text-sm border-b border-border/60 py-1.5">
                            <span className="text-muted-foreground">Connection</span>
                            <span className="font-medium text-primary">Connected</span>
                          </div>
                        ) : (
                          fields.map((f) => {
                            const value = form.getValues(f.key) ?? ""
                            const display = f.secret
                              ? value
                                ? "•".repeat(Math.min(value.length, 12))
                                : hasExisting(f.key)
                                  ? "Unchanged"
                                  : "Not set"
                              : value || "Not set"
                            return (
                              <div
                                key={f.key}
                                className="flex items-center justify-between text-sm border-b border-border/60 py-1.5"
                              >
                                <span className="text-muted-foreground">{f.label}</span>
                                <span className="font-medium">{display}</span>
                              </div>
                            )
                          })
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {submitError && (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    {submitError}
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <div>
                    {step > 0 && (
                      <Button type="button" variant="outline" onClick={goBack} disabled={isSubmitting}>
                        Back
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
                      Cancel
                    </Button>
                    {step < reviewStep ? (
                      <Button type="button" onClick={goNext}>
                        {step === 0 ? "Get started" : "Next"}
                      </Button>
                    ) : (
                      <Button type="button" onClick={() => void handleConfirm()} disabled={isSubmitting}>
                        {isSubmitting ? "Saving…" : actionLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ConnectedConnectorPanel({
  group,
  displayValues,
}: {
  group: PluginConfigGroupDto
  displayValues: Record<string, string>
}) {
  const entries = Object.entries(displayValues).filter(([, v]) => v)

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
        <PlugZap className="w-4 h-4 shrink-0 text-primary" />
        <span>
          <span className="font-medium">{group.title}</span> is already connected. We'll reuse it —
          no need to re-enter anything.
        </span>
      </div>
      {entries.length > 0 && (
        <div className="rounded-lg border border-border p-3 space-y-1">
          {entries.map(([key, value]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground capitalize">{key}</span>
              <span className="font-medium">{value}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Manage this connection from the Integrations page.
      </p>
    </div>
  )
}

function FieldInput({
  field,
  form,
  hasExisting,
  autoFocus,
}: {
  field: PluginConfigFieldDto
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  hasExisting: (key: string) => boolean
  autoFocus?: boolean
}) {
  return (
    <FormField
      control={form.control}
      name={field.key}
      render={({ field: rhfField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <FormControl>
            <Input
              autoFocus={autoFocus}
              type={field.secret ? "password" : "text"}
              autoComplete={field.secret ? "new-password" : "off"}
              placeholder={
                field.secret && hasExisting(field.key)
                  ? "•••••••• (leave blank to keep)"
                  : undefined
              }
              {...rhfField}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
